/**
 * A mentor reviews one student's answers in one module.
 *
 * Left: every question, grouped by assessment, with its status and score.
 * Right: the selected question, the student's answer beside the reference
 * (model answer, correct option, test results), and the marking panel.
 * Save moves on to the next answer that needs review.
 *
 * Keys (outside text fields): ↑/↓ or k/j move between questions.
 * ⌘/Ctrl+Enter saves and moves on.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NavbarLoggedInNew from "../Navbar-new/NavbarLoggedInNew";
import api from "../../api";

const FONT = "Be Vietnam Pro";
const BLUE = "#0172A6";
const INK = { primary: "#16232e", secondary: "#4b5563", muted: "#6b7280" };
const TYPE_LABEL = { p: "Programming", s: "Subjective", o: "MCQ" };
const STATUS = {
  needs_review: { label: "To review", icon: <HourglassEmptyIcon fontSize="inherit" />, color: "#b07d1a" },
  redo_requested: { label: "Redo requested", icon: <AutorenewIcon fontSize="inherit" />, color: "#c2410c" },
  reviewed: { label: "Reviewed", icon: <CheckCircleIcon fontSize="inherit" />, color: "#1f7a45" },
  not_started: { label: "Not answered", icon: <RadioButtonUncheckedIcon fontSize="inherit" />, color: "#9ca3af" },
};
const FILTERS = [
  { key: "all", label: "All" },
  { key: "todo", label: "To review" },
  { key: "s", label: "Subjective" },
  { key: "o", label: "MCQ" },
  { key: "p", label: "Programming" },
];

const errorText = (e, fallback) => e?.response?.data?.error || e?.response?.data?.detail || fallback;
const fmt = (n) => (n === null || n === undefined || Number.isNaN(n) ? "—" : `${Number(Number(n).toFixed(1))}`);

/** What the student scored on a question, as the page shows it. */
const scoreOf = (q) => {
  if (q.review && q.review.manualScore !== null && q.review.manualScore !== undefined) return q.review.manualScore;
  if (q.type === "s" && q.status !== "reviewed") return null;      // not marked yet
  return q.status === "not_started" ? null : q.bestScore;
};

const isMcqWrong = (q) => q.type === "o" && q.submissions?.[0] && !q.submissions[0].isCorrect;

// ─── Left: the question list ───────────────────────────────────────────────

const QuestionRow = ({ q, selected, onSelect }) => {
  const status = STATUS[q.status] || STATUS.not_started;
  const score = scoreOf(q);
  const wrong = isMcqWrong(q);
  return (
    <Box
      role="button" tabIndex={-1} data-testid="review-row" aria-current={selected ? "true" : undefined}
      onClick={() => onSelect(q)}
      sx={{
        display: "flex", gap: 1, alignItems: "flex-start", px: 1.5, py: 1, cursor: "pointer",
        borderLeft: `3px solid ${selected ? BLUE : "transparent"}`,
        background: selected ? "#e8f2f9" : "transparent", "&:hover": { background: selected ? "#e8f2f9" : "#f5f8fb" },
      }}
    >
      <Typography sx={{ width: 22, fontSize: 12, color: INK.muted, pt: "2px" }}>{q.number}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: INK.primary, fontWeight: selected ? 600 : 400,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {q.fullTitle || q.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
          <Typography sx={{ fontSize: 11, color: INK.muted }}>
            {TYPE_LABEL[q.type]}{q.final === false && q.status !== "not_started" ? " · draft" : ""}
          </Typography>
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 11,
                                      color: q.type === "o" && q.status !== "not_started" ? (wrong ? "#c93c3c" : "#1f7a45") : status.color }}>
            {q.type === "o" && q.status !== "not_started"
              ? (wrong ? <><CancelIcon fontSize="inherit" /> Wrong</> : <><CheckCircleIcon fontSize="inherit" /> Correct</>)
              : <>{status.icon} {status.label}</>}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12, color: INK.secondary, whiteSpace: "nowrap", pt: "2px" }}>
        {score === null ? "—" : fmt(score)}/{fmt(q.maxScore)}
      </Typography>
    </Box>
  );
};

// ─── Right: the answer and the reference ───────────────────────────────────

const Section = ({ label, children, tone = "plain", action }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
      <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: INK.muted }}>
        {label}
      </Typography>
      {action}
    </Box>
    <Box sx={{
      p: 1.75, borderRadius: "10px", fontSize: 14, lineHeight: 1.65, color: INK.primary,
      border: "1px solid", borderColor: tone === "answer" ? "#cfe2f1" : tone === "reference" ? "#d6ecdf" : "#e4eaf0",
      background: tone === "answer" ? "#f7fbfe" : tone === "reference" ? "#f6fbf8" : "#fff",
      "& p": { m: 0, mb: 1 }, "& p:last-child": { mb: 0 }, "& pre": { whiteSpace: "pre-wrap" },
    }}>
      {children}
    </Box>
  </Box>
);

const SubjectiveDetail = ({ q }) => {
  const [showModel, setShowModel] = useState(true);
  const answer = q.submissions?.[0]?.answer;
  return (
    <>
      <Section label="Student's answer" tone="answer">
        {answer ? <div dangerouslySetInnerHTML={{ __html: answer }} />
          : <Typography sx={{ color: INK.muted, fontSize: 14 }}>Not answered.</Typography>}
      </Section>
      {q.sampleAnswer && (
        <Section label="Model answer" tone="reference"
                 action={<Button size="small" onClick={() => setShowModel(!showModel)} sx={{ textTransform: "none", py: 0 }}>
                   {showModel ? "Hide" : "Show"}</Button>}>
          <Collapse in={showModel} collapsedSize={22}>
            <Box sx={{ whiteSpace: "pre-wrap" }}>{q.sampleAnswer}</Box>
          </Collapse>
        </Section>
      )}
    </>
  );
};

const McqDetail = ({ q }) => {
  const sub = q.submissions?.[0];
  return (
    <>
      <Section label="Options">
        {(q.options || []).map((opt, i) => {
          const chosen = sub?.selectedOption === opt.text;
          return (
            <Box key={opt.id} sx={{
              display: "flex", gap: 1.25, alignItems: "flex-start", p: 1, mb: 0.75, borderRadius: "8px",
              border: "1px solid", borderColor: opt.isCorrect ? "#9fd5b4" : chosen ? "#f2b8b8" : "#eef2f6",
              background: opt.isCorrect ? "#f0faf4" : chosen ? "#fdf2f2" : "#fff",
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, color: INK.muted, width: 16 }}>{String.fromCharCode(65 + i)}</Typography>
              <Typography sx={{ flex: 1, fontSize: 14 }}>{opt.text}</Typography>
              {opt.isCorrect && <Chip size="small" color="success" variant="outlined" label={chosen ? "Chosen · correct" : "Correct"} />}
              {chosen && !opt.isCorrect && <Chip size="small" color="error" variant="outlined" label="Chosen" />}
            </Box>
          );
        })}
        {!sub && <Typography sx={{ color: INK.muted, fontSize: 13 }}>Not answered.</Typography>}
      </Section>
      {q.explanation && <Section label="Explanation" tone="reference">{q.explanation}</Section>}
    </>
  );
};

const ProgrammingDetail = ({ q }) => {
  const [chosen, setChosen] = useState(0);
  const subs = q.submissions || [];
  const sub = subs[chosen];
  useEffect(() => setChosen(0), [q.id]);
  if (!subs.length) return <Section label="Student's code"><Typography sx={{ color: INK.muted }}>No attempts.</Typography></Section>;
  return (
    <>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1.5 }}>
        {subs.map((s, i) => (
          <Chip key={s.id} clickable onClick={() => setChosen(i)} color={i === chosen ? "primary" : "default"}
                variant={i === chosen ? "filled" : "outlined"} size="small"
                label={`#${s.id} · ${s.passed || 0}/${s.total || 0} passed · ${s.when}`} />
        ))}
      </Box>
      <Section label={`Student's code${sub?.language ? ` (${sub.language})` : ""}`} tone="answer">
        <Box component="pre" sx={{ m: 0, fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}>{sub?.code || "—"}</Box>
      </Section>
    </>
  );
};

// ─── The marking panel ─────────────────────────────────────────────────────

const Verdict = ({ q, onSave, saving, scoreRef }) => {
  const [score, setScore] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const initial = q.review?.manualScore ?? (q.type === "s" ? "" : q.bestScore ?? "");
    setScore(initial === null ? "" : String(initial));
    setFeedback(q.review?.feedback || "");
    setError("");
  }, [q]);

  const submit = (action) => {
    const n = score === "" ? null : Number(score);
    if (n === null && action === "approve" && q.type === "s") return setError("Give a score before approving.");
    if (n !== null && (Number.isNaN(n) || n < 0 || n > q.maxScore)) return setError(`Score must be between 0 and ${q.maxScore}.`);
    setError("");
    onSave({ score: n, feedback, action });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit("approve");
    }
  };

  if (q.status === "not_started") {
    return <Alert severity="info" sx={{ mt: 1 }}>The student hasn't answered this question yet.</Alert>;
  }
  return (
    <Box onKeyDown={onKeyDown} sx={{ borderTop: "1px solid #e4eaf0", pt: 2, mt: 1 }}>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
        <TextField
          inputRef={scoreRef} label="Score" size="small" type="number" value={score} sx={{ width: 140 }}
          onChange={(e) => setScore(e.target.value)} inputProps={{ min: 0, max: q.maxScore, step: 0.5, "aria-label": "Score" }}
          InputProps={{ endAdornment: <InputAdornment position="end">/ {fmt(q.maxScore)}</InputAdornment> }}
        />
        {[0, q.maxScore / 2, q.maxScore].map((v) => (
          <Button key={v} size="small" variant="outlined" onClick={() => setScore(String(v))}
                  sx={{ textTransform: "none", minWidth: 0, px: 1.25 }}>{fmt(v)}</Button>
        ))}
        {q.type !== "s" && (
          <Typography sx={{ fontSize: 12, color: INK.muted }}>auto-graded {fmt(q.bestScore)}</Typography>
        )}
      </Box>
      <TextField label="Feedback for the student" multiline minRows={2} maxRows={6} fullWidth size="small"
                 value={feedback} onChange={(e) => setFeedback(e.target.value)} />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1.5, flexWrap: "wrap" }}>
        <Button onClick={() => submit("redo")} disabled={saving} startIcon={<AutorenewIcon />} sx={{ textTransform: "none" }}>
          Request redo
        </Button>
        <Tooltip title="⌘/Ctrl + Enter">
          <span>
            <Button variant="contained" onClick={() => submit("approve")} disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                    sx={{ textTransform: "none", backgroundColor: BLUE }}>
              Approve &amp; next
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

// ─── The page ──────────────────────────────────────────────────────────────

const MentorSubmissionsReview = () => {
  const { id, topicId, menteeId } = useParams();
  const navigate = useNavigate();
  const selectedModuleId = useSelector((state) => state.module?.selectedModuleId);
  const topic = topicId || selectedModuleId;

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");
  const scoreRef = useRef(null);
  const detailRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const response = await api.get(`course/${topic}/mentor/submissions/${menteeId}/`);
      setData(response.data);
    } catch (e) {
      setError(errorText(e, "Couldn't load this student's answers."));
    }
  }, [topic, menteeId]);

  useEffect(() => { load(); }, [load]);

  const keyOf = (q) => `${q.type}-${q.id}`;
  const questions = useMemo(() => data?.questions || [], [data]);

  // Grouped by assessment, in the mentor's order; questions keep their file order.
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const shown = questions.filter((x) => {
      if (filter === "todo" && x.status !== "needs_review") return false;
      if (["s", "o", "p"].includes(filter) && x.type !== filter) return false;
      return !q || `${x.fullTitle} ${x.title} ${x.question}`.toLowerCase().includes(q);
    });
    const byKey = new Map();
    shown.forEach((x) => {
      const k = x.assessment ? x.assessment.id : "other";
      if (!byKey.has(k)) byKey.set(k, { key: k, title: x.assessment ? x.assessment.title : "Other questions",
                                        order: x.assessment ? x.assessment.order : Infinity, items: [] });
      byKey.get(k).items.push(x);
    });
    const order = { s: 0, o: 1, p: 2 };
    return [...byKey.values()]
      .sort((a, b) => a.order - b.order)
      .map((g) => ({ ...g, items: g.items.sort((a, b) => order[a.type] - order[b.type] || a.id - b.id) }));
  }, [questions, filter, query]);
  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const selected = questions.find((q) => keyOf(q) === selectedKey) || null;

  // Open on the first answer that needs review, else the first question.
  useEffect(() => {
    if (!selectedKey && flat.length) setSelectedKey(keyOf(flat.find((q) => q.status === "needs_review") || flat[0]));
  }, [flat, selectedKey]);

  useEffect(() => { detailRef.current?.scrollTo?.({ top: 0 }); }, [selectedKey]);

  const move = useCallback((step) => {
    if (!flat.length) return;
    const i = flat.findIndex((q) => keyOf(q) === selectedKey);
    const next = flat[Math.min(flat.length - 1, Math.max(0, (i < 0 ? 0 : i) + step))];
    setSelectedKey(keyOf(next));
  }, [flat, selectedKey]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target.isContentEditable) return;
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); move(1); }
      if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); move(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [move]);

  const save = async ({ score, feedback, action }) => {
    if (!selected) return;
    setSaving(true);
    try {
      const sub = selected.submissions?.[0];
      await api.post(`course/${topic}/mentor/submissions/${menteeId}/review/`, {
        question_id: selected.id, question_type: selected.type, submission_id: sub?.id ?? null,
        manual_score: score, feedback, action,
      });
      const reviewed = action === "approve";
      const updated = questions.map((q) => (keyOf(q) === selectedKey ? {
        ...q, status: reviewed ? "reviewed" : "redo_requested",
        review: { feedback, manualScore: score, status: reviewed ? "reviewed" : "needs_redo" },
      } : q));
      const wasPending = selected.status === "needs_review";
      setData((d) => ({ ...d, questions: updated,
                        stats: { ...d.stats, needsReview: Math.max(0, d.stats.needsReview - (wasPending ? 1 : 0)) } }));
      setToast(reviewed ? "Approved." : "Redo requested.");
      // On to the next answer that still needs review, after this one.
      const i = flat.findIndex((q) => keyOf(q) === selectedKey);
      const rest = [...flat.slice(i + 1), ...flat.slice(0, i)];
      const next = rest.find((q) => updated.find((u) => keyOf(u) === keyOf(q))?.status === "needs_review");
      if (next) setSelectedKey(keyOf(next));
    } catch (e) {
      setToast(errorText(e, "Couldn't save the review."));
    } finally {
      setSaving(false);
    }
  };

  // Score so far: auto-graded and marked answers; unmarked essays don't count yet.
  const totals = useMemo(() => {
    const answered = questions.filter((q) => q.status !== "not_started");
    const marked = answered.filter((q) => scoreOf(q) !== null);
    const got = marked.reduce((n, q) => n + Number(scoreOf(q) || 0), 0);
    const markedMax = marked.reduce((n, q) => n + Number(q.maxScore || 0), 0);
    const max = questions.reduce((n, q) => n + Number(q.maxScore || 0), 0);
    const mcq = questions.filter((q) => q.type === "o" && q.status !== "not_started");
    return { got, max, markedMax, answered: answered.length, total: questions.length,
             todo: questions.filter((q) => q.status === "needs_review").length,
             mcqRight: mcq.filter((q) => !isMcqWrong(q)).length, mcq: mcq.length };
  }, [questions]);

  if (error) {
    return (
      <>
        <NavbarLoggedInNew />
        <Box sx={{ maxWidth: 720, mx: "auto", p: 4 }}>
          <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert>
        </Box>
      </>
    );
  }
  if (!data) {
    return (<><NavbarLoggedInNew /><Box sx={{ display: "flex", justifyContent: "center", p: 8 }}><CircularProgress /></Box></>);
  }

  const student = data.mentee || {};
  const pct = (a, b) => (b ? `${Math.round((100 * a) / b)}%` : "—");

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", background: "#F8FAFC", fontFamily: FONT }}>
      <NavbarLoggedInNew />

      {/* Header: who, and how they did */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: { xs: 2, md: 3 }, py: 1.5, borderBottom: "1px solid #e4eaf0",
                 background: "#fff", flexWrap: "wrap" }}>
        <IconButton aria-label="Back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(`/dashboard/workspace/${id}`))}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 17, fontFamily: FONT }}>{student.name || "Student"}</Typography>
          <Typography sx={{ fontSize: 12, color: INK.muted }}>{student.email}</Typography>
        </Box>
        {[
          ["Score so far", `${fmt(totals.got)}/${fmt(totals.max)}`, totals.markedMax < totals.max && totals.answered
            ? `${pct(totals.got, totals.markedMax)} of marked` : pct(totals.got, totals.max)],
          ["Answered", `${totals.answered}/${totals.total}`, null],
          ["MCQ correct", totals.mcq ? `${totals.mcqRight}/${totals.mcq}` : "—", null],
          ["To review", `${totals.todo}`, null],
        ].map(([label, value, hint]) => (
          <Box key={label} sx={{ px: 1.5, borderLeft: "1px solid #eef2f6" }}>
            <Typography sx={{ fontSize: 11, color: INK.muted }}>{label}</Typography>
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: INK.primary }}>{value}</Typography>
            {hint && <Typography sx={{ fontSize: 11, color: INK.muted }}>{hint}</Typography>}
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: { xs: "column", md: "row" } }}>
        {/* Question list */}
        <Box sx={{ width: { xs: "100%", md: 380 }, maxHeight: { xs: 320, md: "none" }, flexShrink: 0, display: "flex",
                   flexDirection: "column", borderRight: { md: "1px solid #e4eaf0" }, background: "#fff" }}>
          <Box sx={{ p: 1.5, borderBottom: "1px solid #eef2f6" }}>
            <TextField size="small" fullWidth placeholder="Search questions" value={query} onChange={(e) => setQuery(e.target.value)}
                       InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
                               sx={{ mt: 1, flexWrap: "wrap", "& .MuiToggleButton-root": { textTransform: "none", py: 0.25, px: 1, fontSize: 12 } }}>
              {FILTERS.map((f) => (
                <ToggleButton key={f.key} value={f.key}>
                  {f.label}{f.key === "todo" && totals.todo ? ` (${totals.todo})` : ""}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {groups.length === 0 && (
              <Typography sx={{ p: 3, color: INK.muted, fontSize: 13, textAlign: "center" }}>
                {filter === "todo" ? "Nothing left to review." : "No questions match."}
              </Typography>
            )}
            {groups.map((g) => (
              <Box key={g.key}>
                <Typography sx={{ position: "sticky", top: 0, zIndex: 1, background: "#f5f8fb", px: 1.5, py: 0.75,
                                  fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: INK.muted, textTransform: "uppercase",
                                  borderBottom: "1px solid #eef2f6" }}>
                  {g.title} · {g.items.length}
                </Typography>
                {g.items.map((q) => (
                  <QuestionRow key={keyOf(q)} q={q} selected={keyOf(q) === selectedKey} onSelect={(x) => setSelectedKey(keyOf(x))} />
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* The selected question */}
        <Box ref={detailRef} sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
          {!selected ? (
            <Typography sx={{ color: INK.muted, textAlign: "center", mt: 6 }}>Pick a question on the left.</Typography>
          ) : (
            <Box sx={{ maxWidth: 920, mx: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                <Chip size="small" label={TYPE_LABEL[selected.type]} />
                {selected.assessment && <Chip size="small" variant="outlined" label={selected.assessment.title} />}
                {selected.final === false && selected.status !== "not_started" && (
                  <Chip size="small" color="warning" variant="outlined" label="Draft: not submitted as final yet" />
                )}
                <Typography sx={{ fontSize: 12, color: INK.muted }}>
                  Q{selected.number} · {fmt(selected.maxScore)} pts
                  {selected.attempts ? ` · ${selected.attempts} attempt${selected.attempts === 1 ? "" : "s"}` : ""}
                  {selected.lastRun && selected.lastRun !== "-" ? ` · ${selected.lastRun}` : ""}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Previous (↑ or k)">
                  <IconButton size="small" aria-label="Previous question" onClick={() => move(-1)}><KeyboardArrowUpIcon /></IconButton>
                </Tooltip>
                <Tooltip title="Next (↓ or j)">
                  <IconButton size="small" aria-label="Next question" onClick={() => move(1)}><KeyboardArrowDownIcon /></IconButton>
                </Tooltip>
              </Box>
              {selected.fullTitle && (
                <Typography sx={{ fontWeight: 600, fontSize: 18, fontFamily: FONT, mb: 1 }}>{selected.fullTitle}</Typography>
              )}
              <Section label="Question">
                <Box sx={{ whiteSpace: "pre-wrap" }}>{selected.question || selected.title}</Box>
              </Section>

              {selected.type === "s" && <SubjectiveDetail q={selected} />}
              {selected.type === "o" && <McqDetail q={selected} />}
              {selected.type === "p" && <ProgrammingDetail q={selected} />}

              <Verdict q={selected} onSave={save} saving={saving} scoreRef={scoreRef} />
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast("")} message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
};

export default MentorSubmissionsReview;
